import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H1, H3, Text } from '@amzn/stencil-react-components/text';
import { Link } from '@amzn/stencil-react-components/link';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { IconInformationFillSmall } from '@amzn/stencil-react-icons';

import { useMessages } from '../../shared/i18n/useMessages';
import { useCurrentUser } from '../../shared/auth';
import { AstraBrandIcon } from '../../shared/ui/AstraBrandIcon';
import {
  PromptContextSection,
  PROMPT_MIN_LENGTH,
} from '../../features/audit-whiz/components/PromptContextSection';
import { SopReferenceSection } from '../../features/audit-whiz/components/SopReferenceSection';
import { GeneratingQuestionsState } from '../../features/audit-whiz/components/GeneratingQuestionsState';
import { useGenerateQuestions } from '../../features/audit-whiz/hooks/useGenerateQuestions';
import { extractSopNumber } from '../../features/audit-whiz/logic/policyUrlValidation';
import { GeneratedQuestionsStatus } from '../../features/audit-whiz/logic/parseGeneratedQuestions';
import type { GenerateToReviewNavState } from '../../features/audit-whiz/logic/reviewTypes';

const PAGE_BUNDLE = 'features.audit-whiz.translations.generateQuestions';

type PageKey =
  | 'breadcrumbCurrent'
  | 'betaBadge'
  | 'promptSectionTitle'
  | 'promptSectionDescription'
  | 'sopSourceBadge'
  | 'sopSourceBadgeLink'
  | 'switchToManualPrompt'
  | 'switchToManualLink'
  | 'cancelButton'
  | 'generateButton'
  | 'generateDisabledHint'
  | 'generatingTitle'
  | 'generatingDescription'
  | 'generatingLoadingText'
  | 'noResultsTitle'
  | 'errorTitle';

/** Questions list route — Cancel and the breadcrumb return here. */
const QUESTIONS_LIST_ROUTE = '/question';
/** Manual question-creation flow — the "switch to manually" link target. */
const MANUAL_CREATE_ROUTE = '/question/question-edit';
/** Review & Publish screen — where the generate action lands (Phase 1 shell). */
const REVIEW_ROUTE = '/question/generate/review';

/**
 * "Create Questions with Astra" generation screen (AuditWhiz WHIZ-001/002).
 *
 * Collects the prompt + optional SOP reference, then calls the Astra generate
 * agent via {@link useGenerateQuestions}. While the request is in flight the
 * form is replaced by an in-page {@link GeneratingQuestionsState} (header
 * stays, with the live agent step). On success it navigates to the Review &
 * Publish screen carrying the generated questions; on `no_results` it stays and
 * surfaces the agent's message; on failure it shows an error banner with retry.
 * The SOP number sent to the agent is extracted from the policy link (see
 * {@link extractSopNumber}).
 *
 * Rendered on desktop through `V2Adapter` (see AppRouter.tsx), which
 * supplies the Stencil, i18n, and auth providers this page needs.
 */
export function GenerateQuestionsPage(): JSX.Element {
  const navigate = useNavigate();
  const { t, f } = useMessages<PageKey>(PAGE_BUNDLE);
  const { businessUnit } = useCurrentUser();

  const [prompt, setPrompt] = useState('');
  const [sopLink, setSopLink] = useState<string | null>(null);
  // The agent's `no_results` message, shown inline (the user stays on this
  // screen to adjust the prompt rather than landing on an empty review). Null
  // when there's no such message to show. Cleared when a new generation starts.
  const [noResultsMessage, setNoResultsMessage] = useState<string | null>(null);

  const { generate, isGenerating, error, cancel } = useGenerateQuestions();

  // Guards navigation against a generation that resolves after the user has
  // left this screen: a late `.then` must not navigate a now-unmounted page.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Abort any in-flight generation when the user leaves the screen.
      cancel();
    };
  }, [cancel]);

  const canGenerate = prompt.trim().length >= PROMPT_MIN_LENGTH && !isGenerating;

  const handleAddSopLink = useCallback((url: string) => setSopLink(url), []);
  const handleRemoveSopLink = useCallback(() => setSopLink(null), []);

  const handleCancel = useCallback(() => {
    void navigate(QUESTIONS_LIST_ROUTE);
  }, [navigate]);

  const handleSwitchToManual = useCallback(() => {
    void navigate(MANUAL_CREATE_ROUTE);
  }, [navigate]);

  const handleGenerate = useCallback(() => {
    // Clear any prior no-results banner before a fresh run (the hook clears its
    // own error on mutate).
    setNoResultsMessage(null);
    // Extract the SOP number from the policy LINK for `agentInputs.sop_number`.
    // No link (or a non-policy URL) → undefined → the agent self-picks a SOP
    // from the prompt. NOTE: when the SOP section gains Search/Upload, branch on
    // the reference TYPE here (Upload sends no sop_number — see extractSopNumber).
    const sopNumber = extractSopNumber(sopLink ?? '');
    generate({
      prompt,
      businessUnitId: businessUnit ?? '',
      ...(sopNumber !== undefined ? { sopNumber } : {}),
    })
      .then((result) => {
        // Re-check intent on completion: if the user navigated away mid-flight,
        // don't act on the resolved result.
        if (!isMountedRef.current) return;
        if (result.status === GeneratedQuestionsStatus.NO_RESULTS) {
          // Stay on the screen and surface the agent's message so the user can
          // adjust the prompt — don't navigate to an empty review.
          setNoResultsMessage(result.message);
          return;
        }
        const state: GenerateToReviewNavState = {
          sopLink,
          generatedQuestions: result.questions,
          generatedMessage: result.message,
          suggestedSop: result.suggestedSop,
        };
        void navigate(REVIEW_ROUTE, { state });
      })
      .catch(() => {
        // Failures are surfaced via the hook's `error` (rendered below). A
        // superseded/aborted turn also rejects here and is intentionally
        // swallowed — the hook already dropped its state.
      });
  }, [generate, prompt, businessUnit, sopLink, navigate]);

  return (
    <Col
      gridGap="dimensions.spacing.300"
      width="100%"
      padding={{
        top: 'dimensions.spacing.300',
        bottom: 'dimensions.spacing.600',
      }}
      data-testid="generate-questions-page"
    >
      {/* Breadcrumb is provided by the V1 desktop shell (AppBreadcrumbs),
            driven by the route path — no in-page breadcrumb here. */}

      {/* Title */}
      <Row gridGap="dimensions.spacing.200" alignItems="center">
        <span data-testid="generate-questions-astra-icon">
          <AstraBrandIcon />
        </span>
        <H1>{t('breadcrumbCurrent')}</H1>
        <Badge type={BadgeType.Primary} value={t('betaBadge')} />
      </Row>

      {isGenerating ? (
        <GeneratingQuestionsState
          title={t('generatingTitle')}
          description={t('generatingDescription')}
          loadingText={t('generatingLoadingText')}
          dataTestId="generate-questions-generating"
        />
      ) : (
        renderForm()
      )}
    </Col>
  );

  /**
   * The prompt/SOP form plus footer actions and any error / no-results banner.
   * Early-return helper (not a nested ternary in JSX) per the tsx-readability
   * rule; shown whenever a generation is NOT in flight.
   */
  function renderForm(): JSX.Element {
    return (
      <>
        {/* Both the hard error and the no-results banner are message-only: the
            footer "Generate" is the single, always-present way to re-run (after
            optionally editing the prompt), so a separate per-banner retry button
            would just duplicate it. The hook clears its own error on the next
            generate, and handleGenerate clears the no-results message. */}
        {error !== null && (
          <MessageBanner
            type={MessageBannerType.Error}
            titleText={t('errorTitle')}
            data-testid="generate-questions-error"
          >
            {error}
          </MessageBanner>
        )}
        {noResultsMessage !== null && (
          <MessageBanner
            type={MessageBannerType.Warning}
            titleText={t('noResultsTitle')}
            data-testid="generate-questions-no-results"
          >
            {noResultsMessage}
          </MessageBanner>
        )}

        {/* One merged card: the prompt and the SOP reference live together under
          a single "Define your audit questions" header, with the Source badge
          in the card's top-right (driven by whether an SOP link is set). */}
        <Card padding="dimensions.spacing.300">
          <Col gridGap="dimensions.spacing.300" width="100%">
            <Row justifyContent="space-between" alignItems="flex-start">
              <Col gridGap="dimensions.spacing.100" flex={1} minWidth={0}>
                <H3>{t('promptSectionTitle')}</H3>
                <Text color="color.text.secondary">{t('promptSectionDescription')}</Text>
              </Col>
              <Badge
                type={sopLink !== null ? BadgeType.Positive : BadgeType.Neutral}
                value={
                  sopLink !== null
                    ? f('sopSourceBadgeLink', { url: sopLink })
                    : t('sopSourceBadge')
                }
                data-testid="generate-questions-source-badge"
              />
            </Row>

            <PromptContextSection
              value={prompt}
              onChange={setPrompt}
              dataTestId="generate-questions-prompt-input"
            />

            <SopReferenceSection
              link={sopLink}
              onAddLink={handleAddSopLink}
              onRemoveLink={handleRemoveSopLink}
              dataTestId="generate-questions-sop"
            />
          </Col>
        </Card>

        {/* Footer actions */}
        <Row justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Row gridGap="dimensions.spacing.100" alignItems="center">
            <Text color="color.text.secondary">{t('switchToManualPrompt')}</Text>
            <Link
              onClick={handleSwitchToManual}
              data-testid="generate-questions-switch-manual"
            >
              {t('switchToManualLink')}
            </Link>
          </Row>

          <Col gridGap="dimensions.spacing.50" alignItems="flex-end">
            <Row gridGap="dimensions.spacing.200" alignItems="center">
              <Button
                variant={ButtonVariant.Tertiary}
                onClick={handleCancel}
                data-testid="generate-questions-cancel"
              >
                {t('cancelButton')}
              </Button>
              <Button
                variant={ButtonVariant.Primary}
                disabled={!canGenerate}
                onClick={handleGenerate}
                data-testid="generate-questions-generate"
              >
                {t('generateButton')}
              </Button>
            </Row>
            {!canGenerate && (
              <Row
                gridGap="dimensions.spacing.50"
                alignItems="center"
                data-testid="generate-questions-disabled-hint"
              >
                <IconInformationFillSmall aria-hidden="true" />
                <Text fontSize="T100" color="color.text.secondary">
                  {t('generateDisabledHint')}
                </Text>
              </Row>
            )}
          </Col>
        </Row>
      </>
    );
  }
}
